import { apiErrorText } from "pages/PayRoll/PayInputs/ShiftEarnings/shiftEarningsApi";
import {
  first,
  fromTemplateRowApi,
  label,
  listOf,
  personName,
  query,
  request,
  toBool,
  toDay,
  toNumber,
  toComponentRows,
  unwrap,
  versionNumber,
} from "../SalaryTemplates/salaryTemplateApi";
import { normalizeCategory } from "../SalaryComponents/salaryComponentApi";

// Payroll > Salary > Assign Salary on the payroll/salary/ APIs (spec section
// 6): salary-assign (list / single / create / edit / delete draft),
// assign-not-configured, get-salary-assign-choices and salary-request-action
// (submit / approve / reject / cancel).
//
// The API doc has no sample responses, so every reader below accepts the
// likely field names and falls back to safe defaults. Check them against a
// live response.

const BASE = "payroll/salary/";
const ASSIGN = `${BASE}salary-assign/`;
const LEVEL_COUNT = 2;
const LEVEL_ROLES = { 1: "HR Manager", 2: "Finance Manager" };

export const idText = (value) => (value === undefined || value === null || value === "" ? null : String(value));
// Ids go back to the server as numbers when they are numbers.
export const toServerId = (value) => (/^\d+$/.test(String(value ?? "")) ? Number(value) : value);
export const objectOf = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});
export const arrayOf = (value) => (Array.isArray(value) ? value : []);

// "Monthly · 1st to last day" from a string or a { name, description } object.
export const cycleText = (value) => {
  if (!value) return "";
  if (typeof value !== "object") return String(value);
  return [first(value.name, value.frequency, value.label, value.pay_cycle), first(value.description, value.range, value.period, value.days)]
    .filter(Boolean)
    .join(" · ");
};

// Rows of a given kind wherever the answer keeps them (a plain list, or
// under data / results / requests next to card counts and banners).
export const findRows = (value, test, depth = 0) => {
  if (depth > 3 || value == null || typeof value !== "object") return [];
  if (Array.isArray(value)) {
    return value.some(test) ? value.filter(test) : value.flatMap((item) => findRows(item, test, depth + 1));
  }
  return Object.values(value).flatMap((item) => findRows(item, test, depth + 1));
};
export const uniqueBy = (rows, key) => {
  const seen = new Set();
  return rows.filter((row) => {
    const value = key(row);
    if (value == null || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

// ---------- Status ----------

// Server status text -> { status: Draft | Pending | Rejected | Cancelled |
// Approved, level, upcoming }.
export const readStatus = (raw, row) => {
  const text = String(raw || "").toLowerCase();
  const levelInText = String(raw || "").match(/L\s*(\d)/i);
  const level =
    toNumber(first(row.level, row.current_level, row.approval_level, row.pending_level)) ??
    (levelInText ? Number(levelInText[1]) : null);
  if (text.includes("draft")) return { status: "Draft", level: 0, upcoming: false };
  if (text.includes("reject")) return { status: "Rejected", level: level || 1, upcoming: false };
  if (text.includes("cancel") || text.includes("withdraw")) return { status: "Cancelled", level: level ?? 0, upcoming: false };
  if (text.includes("pending")) return { status: "Pending", level: level || 1, upcoming: false };
  if (/approved|active|applied|effective|complete/.test(text)) {
    return { status: "Approved", level: LEVEL_COUNT, upcoming: text.includes("upcoming") ? true : text.includes("applied") || text.includes("active") ? false : null };
  }
  if (text.includes("upcoming")) return { status: "Approved", level: LEVEL_COUNT, upcoming: true };
  return { status: raw ? String(raw) : "Draft", level: level ?? 0, upcoming: null };
};

// ---------- People ----------

const isNumberId = (value) => /^\d+$/.test(String(value ?? ""));
const joinName = (source) => [source.first_name, source.middle_name, source.last_name].filter(Boolean).join(" ").trim() || undefined;

export const personFields = (row) => {
  const person = objectOf(first(row.employee, row.user, row.user_details, row.employee_details));
  const userId = first(row.user_id, row.userid, person.user_id, person.id, row.pk);
  // employee_id is the employee code when it isn't a number (or when
  // user_id carries the id).
  const employeeIdIsCode = row.employee_id != null && (!isNumberId(row.employee_id) || userId != null);
  return {
    id: idText(first(userId, !employeeIdIsCode ? row.employee_id : undefined, row.id, row.employee_id)),
    name:
      first(
        row.employee_name, row.user_name, row.full_name, row.emp_name, row.name, joinName(row),
        person.name, person.full_name, person.user_name, person.employee_name, joinName(person),
      ) || "",
    code: String(first(row.employee_code, row.emp_code, row.employee_no, person.employee_code, person.employee_id, employeeIdIsCode ? row.employee_id : undefined) ?? ""),
    department: first(row.department_name, typeof row.department === "string" ? row.department : undefined, row.department?.name, person.department_name, person.department) || "",
    position: first(row.designation_name, row.position, typeof row.designation === "string" ? row.designation : undefined, row.designation?.name, person.designation) || "",
    joined: toDay(first(row.date_of_joining, row.joining_date, row.doj, row.joined, person.date_of_joining, person.joining_date)),
    group: first(row.payroll_group_name, typeof row.payroll_group === "string" ? row.payroll_group : undefined, row.payroll_group?.name) || "",
  };
};

// An employee-like row: some id plus some name.
export const isPersonRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  const fields = personFields(row);
  // Plus something only an employee has, so filter lists ({ id, name }
  // departments) don't count.
  const employeeOnly =
    fields.code || fields.joined ||
    first(row.user_id, row.employee_id, row.employee_name, row.user_name, row.days_waiting, row.waiting_days, row.employee, row.user) != null;
  return Boolean(fields.id && fields.name && employeeOnly);
};

const fromPersonApi = (row) => ({
  ...personFields(row),
  waiting: toNumber(first(row.days_waiting, row.waiting_days, row.waiting, row.days)),
});

// ---------- Breakdown ----------

// Per-employee breakdown (gross, the category totals, NET before statutory)
// in the shape the screen's breakdown list reads, or null. A row's own
// category wins over its list's ("earnings" also carries Additional
// Earnings, "deductions" Additional Deductions).
export const fromBreakdownApi = (row, lines) => {
  const source = first(row.breakdown, row.salary_breakdown, row.preview, row.calculation);
  const base = source && typeof source === "object" ? source : row;
  const item = (entry, category) => ({
    code: String(first(entry.code, entry.component_code, entry.component_id, entry.name) ?? ""),
    name: first(entry.name, entry.component_name, entry.code) || "",
    category: normalizeCategory(first(entry.category, entry.component_category)) || category,
    amount: toNumber(first(entry.amount, entry.value, entry.computed_amount, entry.amount_at_gross)),
  });
  const list = (value) => (Array.isArray(value) ? value : undefined);
  let items = [
    ...arrayOf(first(base.earnings_rows, list(base.earnings))).map((entry) => item(entry, "Earnings")),
    ...arrayOf(first(base.deductions_rows, list(base.deductions))).map((entry) => item(entry, "Deductions")),
    ...arrayOf(base.employer_contributions).map((entry) => item(entry, "Employer Contribution")),
    ...arrayOf(base.reimbursements).map((entry) => item(entry, "Reimbursement")),
    ...arrayOf(base.calculations).map((entry) => item(entry, "Calculation")),
  ];
  if (!items.length && lines?.some((line) => line.server_amount != null)) {
    items = lines.map((line) => ({ code: line.code, name: line.name, category: line.category, amount: line.server_amount }));
  }
  const sum = (category) => items.filter((entry) => entry.category === category).reduce((total, entry) => total + (entry.amount || 0), 0);
  const earnings = toNumber(first(base.total_earnings, base.earnings_total, base.gross_total, typeof base.earnings === "number" ? base.earnings : undefined));
  const deductions = toNumber(first(base.total_deductions, base.deductions_total, typeof base.deductions === "number" ? base.deductions : undefined));
  const net = toNumber(first(base.net_before_statutory, base.net, base.net_pay));
  if (!items.length && net == null) return null;
  const earningsValue = earnings ?? sum("Earnings");
  const deductionsValue = deductions ?? sum("Deductions");
  const otherEarnings = sum("Additional Earnings");
  const otherDeductions = sum("Additional Deductions");
  const employer = sum("Employer Contribution");
  return {
    items: items.filter((entry) => entry.code),
    values: Object.fromEntries(items.filter((entry) => entry.code).map((entry) => [entry.code, entry.amount])),
    earnings: earningsValue,
    otherEarnings,
    deductions: deductionsValue,
    otherDeductions,
    employer,
    reimbursement: sum("Reimbursement"),
    ctc: earningsValue + otherEarnings + employer,
    net: net ?? earningsValue + otherEarnings - deductionsValue - otherDeductions,
    balanced: true,
    unresolved: [],
    errors: [],
  };
};

// ---------- Requests ----------

const fromRequestEmployee = (row) => {
  const structure = first(row.components, row.rows, row.structure, row.lines);
  const lines = Array.isArray(structure) && structure.length ? structure.map(fromTemplateRowApi) : null;
  const customized = toBool(
    first(row.is_customized, row.customized),
    /custom/i.test(String(first(row.structure_type, row.structure_label, typeof row.structure === "string" ? row.structure : "") || "")),
  );
  return {
    ...personFields(row),
    employee_id: personFields(row).id,
    gross: toNumber(first(row.gross, row.new_gross, row.gross_salary)) ?? "",
    effective_from: toDay(first(row.effective_from, row.effective_date)),
    customized,
    // Only a customized employee carries its own lines; everyone else
    // follows the template version.
    lines: customized ? lines : null,
    structure_lines: lines,
    breakdown: fromBreakdownApi(row, lines),
  };
};

const historyTitle = (action, level) => {
  const text = action.toLowerCase();
  const at = level ? ` at L${level}` : "";
  if (text.includes("approve")) return `Approved${at}`;
  if (text.includes("reject")) return `Rejected${at}`;
  if (text.includes("resubmit")) return "Resubmitted";
  if (text.includes("submit")) return "Submitted";
  if (text.includes("cancel")) return "Cancelled";
  if (text.includes("import")) return "Imported";
  if (text.includes("delete")) return "Deleted";
  if (text.includes("create") || text.includes("draft")) return "Draft created";
  if (text.includes("update") || text.includes("save") || text.includes("edit")) return "Draft saved";
  return action || "Updated";
};

export const fromHistoryApi = (item) => {
  if (typeof item === "string") return { title: item, by: "", at: "", note: "", level: null };
  const action = String(first(item.action, item.decision, item.event, item.status, item.activity) || "");
  const level = toNumber(first(item.level, item.approval_level));
  return {
    title: first(item.title, item.label) || historyTitle(action, level),
    by: personName(first(item.by, item.actor, item.action_by_name, item.action_by, item.user_name, item.user, item.created_by_name, item.created_by)),
    at: first(item.at, item.acted_at, item.action_on, item.action_at, item.created_at, item.created_on, item.date) || "",
    note: first(item.note, item.remarks, item.comments, item.comment, item.reason) || "",
    level,
  };
};

const namesOf = (value) =>
  (Array.isArray(value) ? value.map((item) => (typeof item === "string" ? item : personFields(objectOf(item)).name)) : String(value || "").split(","))
    .map((name) => String(name).trim())
    .filter(Boolean);

// List row / single -> the request object the screen uses.
export const fromRequestApi = (raw) => {
  const row = { ...objectOf(raw.configuration), ...raw };
  const group = objectOf(row.payroll_group);
  const template = objectOf(row.template);
  const { status, level, upcoming } = readStatus(first(row.status, row.request_status, row.status_name, row.status_label), row);
  const employees = arrayOf(first(row.employees, row.employee_rows, row.employee_details, row.details))
    .filter((item) => item && typeof item === "object")
    .map(fromRequestEmployee);
  const history = arrayOf(first(row.history, row.approval_history, row.logs, row.timeline, row.activity))
    .map(fromHistoryApi)
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const rejectEntry = history.find((entry) => entry.title.startsWith("Rejected"));
  const rejection = objectOf(row.rejection);
  const rejectionLevel = toNumber(first(rejection.level, row.rejected_level, rejectEntry?.level)) ?? level;
  const id = idText(first(row.request_id, row.id));
  return {
    id,
    number: first(row.request_no, row.request_number, row.number, row.reference_no, row.request_code) || (id ? `#${id}` : ""),
    type: /import/i.test(String(first(row.type, row.request_type, row.source) || "")) ? "Import" : "Manual",
    title: first(row.title, row.file_name) || "",
    template_id: idText(first(row.template_id, template.id)),
    template_name: first(row.template_name, template.name) || "",
    template_version: versionNumber(first(row.template_version, row.template_version_number, row.version, template.version, template.version_no)) ?? 1,
    template_version_id: first(row.template_version_id, template.version_id) ?? null,
    payroll_group_id: idText(first(row.payroll_group_id, group.id)),
    payroll_group: first(row.payroll_group_name, group.name, typeof row.payroll_group === "string" ? row.payroll_group : undefined) || "",
    pay_cycle: cycleText(first(row.pay_cycle, row.pay_cycle_name, group.pay_cycle, group.cycle)),
    salary_type: first(row.salary_type, row.pay_basis, template.pay_basis) || "Monthly",
    status,
    level,
    upcoming,
    employees,
    employee_count: toNumber(first(row.employee_count, row.employees_count, row.total_employees, row.no_of_employees)) ?? employees.length,
    employee_names: employees.length ? employees.map((item) => item.name).filter(Boolean) : namesOf(first(row.employee_names, row.names)),
    customized_count: toNumber(first(row.customized_count, row.customized_employees)),
    total_gross: toNumber(first(row.total_gross, row.gross_total, row.total_new_gross)),
    gross: toNumber(row.gross),
    effective_from: toDay(first(row.effective_from, row.effective_from_min, row.start_date)),
    effective_to: toDay(first(row.effective_to, row.effective_from_max, row.end_date)),
    comments: first(row.comments, row.comment) || "",
    submitted_at: first(row.submitted_at, row.submitted_on) || "",
    approved_at: first(row.approved_at, row.approved_on) || "",
    imported_at: first(row.imported_at, row.imported_on) || "",
    saved_at: first(row.updated_at, row.modified_at, row.updated_on, row.created_at, row.created_on) || "",
    saved_by: personName(first(row.updated_by_name, row.updated_by, row.created_by_name, row.created_by, row.submitted_by)),
    created_by: personName(first(row.created_by_name, row.created_by, row.raised_by_name, row.raised_by, row.submitted_by)),
    rejection:
      status === "Rejected"
        ? {
            level: rejectionLevel,
            role: LEVEL_ROLES[rejectionLevel] || "Approver",
            by: personName(first(rejection.by, row.rejected_by_name, row.rejected_by)) || rejectEntry?.by || "the approver",
            at: first(rejection.at, row.rejected_at, row.rejected_on) || rejectEntry?.at || "",
            reason: first(rejection.reason, row.rejection_reason, row.reject_reason, row.remarks) || rejectEntry?.note || "",
          }
        : null,
    history,
    // Flags the server may send; undefined means "not told", and the server
    // still refuses what the user can't do.
    can_approve: toBool(first(row.can_approve, row.is_approver), undefined),
    pending_mine: status === "Pending" && toBool(first(row.pending_my_approval, row.is_pending_my_approval, row.my_approval, row.can_approve), false),
    can_cancel: toBool(row.can_cancel, undefined),
    can_delete: toBool(row.can_delete, undefined),
    can_edit: toBool(row.can_edit, undefined),
    details_loaded: false,
  };
};

const isRequestRow = (row) =>
  row && typeof row === "object" && !Array.isArray(row) &&
  first(row.request_id, row.id) != null &&
  first(row.request_id, row.request_no, row.request_number, row.request_status, row.template_id, row.payroll_group_id) != null;

// Card counts next to the rows, wherever they sit (beside data: [...] or
// inside data: {...}).
export const cardsOf = (data) => {
  const value = unwrap(data);
  return [value, data]
    .map((source) => (source && typeof source === "object" && !Array.isArray(source) ? first(source.cards, source.counts, source.summary, source.status_counts, source.card_counts) : undefined))
    .find((cards) => cards != null);
};

// Card counts from an object ({ pending: 2 }) or a list ([{ key, count }]).
export const readCardCounts = (value) => {
  const counts = {};
  const keyOf = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  if (Array.isArray(value)) {
    value.forEach((item) => {
      const count = toNumber(first(item?.count, item?.value, item?.total));
      if (count != null) counts[keyOf(first(item?.key, item?.name, item?.label, item?.title))] = count;
    });
  } else if (value && typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => {
      const count = toNumber(typeof item === "object" ? first(item?.count, item?.value) : item);
      if (count != null) counts[keyOf(key)] = count;
    });
  }
  return counts;
};

const readCards = (value) => {
  const counts = readCardCounts(value);
  const pick = (...keys) => keys.map((key) => counts[key]).find((count) => count != null) ?? null;
  return {
    all: pick("all", "total", "all_requests"),
    Approved: pick("approved"),
    Rejected: pick("rejected"),
    Upcoming: pick("upcoming", "approved_upcoming"),
  };
};

// Requests tab: rows, card counts and the not-configured banner.
export const listAssignRequests = async (domain, companyId, signal) => {
  const data = await request(domain, query(ASSIGN, { company_id: companyId }), { signal });
  if (!data || typeof data !== "object") throw new Error("The server returned an invalid salary request list.");
  const value = unwrap(data);
  const list = listOf(data, ["requests", "salary_requests", "assignments"]) || findRows(value, isRequestRow);
  const banner = first(value?.not_configured, value?.not_configured_banner, value?.banner, data?.not_configured, data?.not_configured_banner, data?.banner);
  return {
    requests: uniqueBy(list.filter(isRequestRow), (row) => idText(first(row.request_id, row.id))).map(fromRequestApi),
    counts: readCards(cardsOf(data)),
    notConfigured: {
      count: toNumber(first(typeof banner === "number" ? banner : objectOf(banner).count, value?.not_configured_count, data?.not_configured_count)),
      oldest: toDay(first(objectOf(banner).oldest_joining_date, objectOf(banner).oldest_joined, objectOf(banner).oldest_date_of_joining, value?.oldest_joining_date, data?.oldest_joining_date)),
    },
  };
};

// One request with every employee's breakdown and the approval history.
export const getAssignRequest = async (domain, id, signal) => {
  const data = unwrap(await request(domain, query(ASSIGN, { request_id: id }), { signal }));
  const row = Array.isArray(data) ? data[0] : first(data?.request, data?.assignment) || data;
  if (!row || typeof row !== "object") throw new Error("The server returned an invalid salary request.");
  return { ...fromRequestApi(row), details_loaded: true };
};

// Not configured tab: active employees with no salary.
export const listNotConfigured = async (domain, companyId, signal) => {
  const data = await request(domain, query(`${BASE}assign-not-configured/`, { company_id: companyId }), { signal });
  const value = unwrap(data);
  const list = listOf(data, ["employees", "not_configured", "users"]) || findRows(value, isPersonRow);
  return uniqueBy(list.filter(isPersonRow).map(fromPersonApi), (row) => row.id);
};

// Configure step: salary types, templates for the salary type, payroll
// groups with their pay cycle.
export const getAssignChoices = async (domain, companyId, salaryType, signal) => {
  const data = unwrap(await request(domain, query(`${BASE}get-salary-assign-choices/`, { company_id: companyId, salary_type: salaryType }), { signal }));
  const salaryTypes = arrayOf(first(data?.salary_types, data?.salary_type_choices, data?.pay_basis_choices)).map(label).filter(Boolean);
  const templates = arrayOf(first(data?.templates, data?.template_choices, data?.salary_templates))
    .map((item) => ({
      id: idText(first(item.id, item.template_id, item.value)),
      name: first(item.name, item.template_name, item.label) || "",
      pay_basis: first(item.pay_basis, item.salary_type) || salaryType,
      version: versionNumber(first(item.current_version, item.version, item.version_number)) ?? 1,
      version_id: first(item.version_id, item.template_version_id, item.current_version_id) ?? null,
    }))
    .filter((item) => item.id);
  const groups = arrayOf(first(data?.payroll_groups, data?.payroll_group_choices, data?.groups))
    .map((item) => ({
      id: idText(first(item.id, item.payroll_group_id, item.value)),
      name: first(item.name, item.payroll_group_name, item.group_name, item.label) || "",
      cycle: cycleText(first(item.pay_cycle, item.cycle, item.pay_cycle_name, item.pay_cycle_label)),
    }))
    .filter((item) => item.id);
  return { salaryTypes: salaryTypes.length ? salaryTypes : null, templates, groups };
};

// ---------- Save / actions ----------

// A customized employee's rows: component_id for rows that match the
// template, the calculation for changed or added rows, is_balancing on the
// balancing earning.
export const customComponentRows = (baseLines, lines) => {
  const same = (a, b) =>
    a.method === b.method &&
    (a.method !== "Fixed Amount" || Number(a.amount) === Number(b.amount)) &&
    (a.method !== "Percentage" || (Number(a.percentage) === Number(b.percentage) && (a.base || "GROSS") === (b.base || "GROSS"))) &&
    (a.method !== "Formula" || String(a.formula || "").trim() === String(b.formula || "").trim());
  return toComponentRows(lines, (line) => {
    const base = baseLines.find((item) => item.code === line.code);
    return !base || !same(base, line);
  });
};

// The form -> the POST / PUT body.
export const toAssignBody = (form, { companyId, versionId, submit, componentsFor }) => {
  const same = form.gross_mode === "same";
  const body = {
    ...(form.request_id ? { request_id: toServerId(form.request_id) } : {}),
    company_id: Number(companyId),
    salary_type: form.salary_type,
    template_id: toServerId(form.template_id),
    ...(versionId != null ? { template_version_id: toServerId(versionId) } : {}),
    payroll_group_id: toServerId(form.payroll_group_id),
    gross: Number(same ? form.same_gross : form.employees[0]?.gross) || 0,
    employees: form.employees.map((row) => {
      const out = { user_id: toServerId(row.employee_id) };
      if (row.gross !== "" && row.gross != null) out.gross = Number(row.gross);
      if (row.effective_from) out.effective_from = row.effective_from;
      const components = componentsFor(row);
      if (components) out.components = components;
      return out;
    }),
    comments: String(form.comments || "").trim(),
    submit: Boolean(submit),
  };
  return body;
};

// POST (new) or PUT (request_id set). Returns the request id.
export const saveAssignRequest = async (domain, body, signal) => {
  const data = unwrap(await request(domain, ASSIGN, { method: body.request_id ? "PUT" : "POST", body, signal }));
  return idText(first(data?.request_id, data?.id, data?.request?.id, data?.request?.request_id)) ?? idText(body.request_id);
};

// Soft delete of a Draft, by the person who created it.
export const deleteAssignDraft = (domain, id, signal) =>
  request(domain, ASSIGN, { method: "PUT", body: { request_id: toServerId(id), is_deleted: true }, signal });

// submit / approve / reject / cancel. Reject needs remarks.
export const assignRequestAction = async (domain, id, action, remarks, signal) => {
  const data = await request(domain, `${BASE}salary-request-action/`, {
    method: "POST",
    body: { request_id: toServerId(id), action, ...(remarks ? { remarks } : {}) },
    signal,
  });
  return apiErrorText(first(data?.message, data?.detail)) || "";
};
